import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Pin, PinOff, Search, BadgeCheck, Loader2, Radio, X, ChevronDown, Trophy } from "lucide-react";
import { MOVES } from "../../data/moves";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED = "#C41E24", GOLD = "#D4AF37";
const CHIPS = [{ id: "", name: "Session Clip" }, ...MOVES.filter((m) => ["elastico", "cruyff-turn", "v-pull", "scissors", "la-croqueta", "toe-taps", "drag-back", "l-turn", "maradona"].includes(m.id))];
const buzz = (p) => { try { navigator.vibrate?.(p); } catch (e) { /* unsupported */ } };

const RecordButton = ({ token, player, drill, onUploaded }) => {
  const inputRef = useRef();
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    buzz(20);
    const fd = new FormData();
    fd.append("file", file, file.name || "clip.mp4");
    fd.append("move_label", drill?.name || "Session Clip");
    fd.append("move_id", drill?.id || "");
    setPct(0);
    try {
      await axios.post(`${API}/coach/players/${player.id}/clip`, fd, { headers: { Authorization: `Bearer ${token}` }, onUploadProgress: (e) => setPct(e.total ? Math.round((e.loaded / e.total) * 100) : 50) });
      buzz([40, 60, 40]);
      toast.success(`Clip saved to ${player.name?.split(" ")[0] || "player"}'s Hub`, { description: "Coach-Verified · Engine scan started." });
      onUploaded();
    } catch (err) {
      buzz(200);
      toast.error(err?.response?.data?.detail || "Upload failed — try again.");
    }
    setPct(null);
    if (inputRef.current) inputRef.current.value = "";
  };
  return (
    <>
      <input ref={inputRef} data-testid={`onfield-clip-input-${player.id}`} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <button data-testid={`onfield-record-${player.id}`} onClick={() => inputRef.current?.click()} disabled={pct !== null} aria-label={`Record session clip for ${player.name}`}
        className="relative w-full h-16 rounded-lg flex items-center justify-center gap-3 font-headings text-xl uppercase tracking-wide text-white overflow-hidden active:scale-[0.98] transition-transform disabled:opacity-90"
        style={{ background: RED, boxShadow: `0 10px 30px ${RED}55, inset 0 1px 0 rgba(255,255,255,0.25)` }}>
        {pct !== null && <span className="absolute inset-y-0 left-0 bg-white/25 transition-[width] duration-200" style={{ width: `${pct}%` }} />}
        <span className="relative flex items-center gap-3">
          {pct !== null ? <><Loader2 size={22} className="animate-spin" /> Uploading {pct}%</> : <><span className="w-4 h-4 rounded-full bg-white animate-pulse" /> <Video size={22} /> Record Session Clip</>}
        </span>
      </button>
    </>
  );
};

const CHALLENGES = ["Toe Taps", "Foundations", "Sole Rolls", "Pull Push", "V Pull", "Juggling"];

// Block G · Success Close: log the 60-second challenge score straight to the player (+Hub points, PB tracking).
const BlockGForm = ({ token, player, onLogged }) => {
  const [open, setOpen] = useState(false);
  const [challenge, setChallenge] = useState(CHALLENGES[0]);
  const [custom, setCustom] = useState("");
  const [score, setScore] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const name = challenge === "custom" ? custom.trim() : challenge;
  const best = player.block_g_bests?.[name];
  const save = async () => {
    const n = Number(score);
    if (!name) return toast.error("Name the challenge.");
    if (!score || isNaN(n) || n < 0) return toast.error("Enter the score.");
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/coach/players/${player.id}/block-g`, { challenge: name, score: n, unit: name === "Juggling" ? "juggles" : "reps", note }, { headers: { Authorization: `Bearer ${token}` } });
      buzz(data.is_pb ? [40, 60, 40, 60, 80] : [30]);
      toast.success(data.is_pb ? `New PB · ${name} ${n}` : `Logged · ${name} ${n}`, { description: `+${data.points} Hub points${data.prev_best != null ? ` · previous best ${data.prev_best}` : ""} · visible in ${player.name?.split(" ")[0] || "player"}'s Hub` });
      setScore(""); setNote(""); setOpen(false);
      onLogged();
    } catch (err) {
      buzz(200);
      toast.error(err?.response?.data?.detail || "Couldn't log the score.");
    }
    setSaving(false);
  };
  return (
    <div data-testid={`onfield-blockg-${player.id}`} className="rounded-lg border" style={{ borderColor: `${GOLD}44`, background: "#0d0d0d" }}>
      <button data-testid={`onfield-blockg-toggle-${player.id}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full h-12 px-3 flex items-center gap-2 text-left">
        <span className="w-7 h-7 rounded-sm flex items-center justify-center font-cover text-base text-black shrink-0" style={{ background: GOLD }}>G</span>
        <span className="font-headings uppercase tracking-wide text-sm leading-none">Success Close · Log 60-Sec Challenge</span>
        {player.block_g_today > 0 && <span data-testid={`onfield-blockg-count-${player.id}`} className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ color: GOLD, border: `1px solid ${GOLD}66` }}>{player.block_g_today} today</span>}
        <ChevronDown size={16} className={`ml-auto text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div data-testid={`onfield-blockg-chips-${player.id}`} className="flex flex-wrap gap-1.5">
            {[...CHALLENGES, "custom"].map((c) => (
              <button key={c} data-testid={`onfield-blockg-chip-${player.id}-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setChallenge(c)} aria-pressed={challenge === c}
                className={`h-9 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-colors ${challenge === c ? "bg-[#C41E24] text-white border-[#C41E24]" : "border-white/15 text-white/60"}`}>{c === "custom" ? "Custom…" : c}</button>
            ))}
          </div>
          {challenge === "custom" && <input data-testid={`onfield-blockg-custom-${player.id}`} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Challenge name (e.g. Elastico Ladder)" className="w-full h-11 bg-[#141414] border border-white/10 rounded-lg px-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input data-testid={`onfield-blockg-score-${player.id}`} type="number" inputMode="decimal" min="0" value={score} onChange={(e) => setScore(e.target.value)} placeholder="Score"
                className="w-full h-14 bg-[#141414] border rounded-lg px-3 text-white font-cover text-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" style={{ borderColor: `${GOLD}55` }} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[9px] uppercase tracking-widest text-white/40">{name === "Juggling" ? "juggles" : "reps"} / 60s</span>
            </div>
            <div data-testid={`onfield-blockg-best-${player.id}`} className="w-24 h-14 rounded-lg border border-white/10 flex flex-col items-center justify-center shrink-0">
              <span className="font-mono text-[8px] uppercase tracking-widest text-white/40">Best</span>
              <span className="font-cover text-xl leading-none" style={{ color: best != null ? GOLD : "rgba(255,255,255,0.3)" }}>{best != null ? best : "—"}</span>
            </div>
          </div>
          <input data-testid={`onfield-blockg-note-${player.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Named success (optional) — e.g. 'Kept head up the whole minute'" className="w-full h-11 bg-[#141414] border border-white/10 rounded-lg px-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
          <button data-testid={`onfield-blockg-save-${player.id}`} onClick={save} disabled={saving}
            className="w-full h-12 rounded-lg flex items-center justify-center gap-2 font-headings text-lg uppercase tracking-wide text-black active:scale-[0.98] transition-transform disabled:opacity-70" style={{ background: GOLD, boxShadow: `0 8px 24px ${GOLD}44` }}>
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Trophy size={18} />} Log to Player · +25 pts
          </button>
        </div>
      )}
    </div>
  );
};

// ON-FIELD tab: today's pinned roster + one-thumb clip recording that lands as a Coach-Verified proof.
export default function OnField({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState({ date: "", players: [] });
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => axios.get(`${API}/coach/onfield`, auth).then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); axios.get(`${API}/coach/players`, auth).then(({ data: d }) => setAll(d)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pinnedIds = useMemo(() => new Set(data.players.map((p) => p.id)), [data]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? all.filter((p) => !pinnedIds.has(p.id) && ((p.name || "") + " " + (p.email || "")).toLowerCase().includes(s)).slice(0, 6) : [];
  }, [q, all, pinnedIds]);

  const pin = async (p, pinned) => {
    setBusy(p.id);
    try {
      await axios.put(`${API}/coach/players/${p.id}/pin`, { pinned }, auth);
      buzz(15);
      setQ("");
      await load();
    } catch (e) { toast.error("Couldn't update pin."); }
    setBusy("");
  };
  const drill = CHIPS.find((c) => c.id === chip);

  return (
    <div data-testid="onfield-tab" className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Radio size={18} className="text-[#C41E24] animate-pulse" /><h2 className="font-headings text-2xl uppercase tracking-wide">On-Field · Rapid Access</h2></div>
          <p className="text-xs text-white/45 mt-1">Today's pinned players only ({data.date}). Pins clear at midnight. Tap RECORD to open the camera — the clip lands in their Hub as <span className="text-[#D4AF37]">Coach-Verified</span>.</p>
        </div>
        <span data-testid="onfield-count" className="shrink-0 font-cover text-3xl" style={{ color: GOLD }}>{data.players.length}</span>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input data-testid="onfield-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pin a player for today…" className="w-full h-12 bg-[#141414] border border-white/10 rounded-lg pl-10 pr-10 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
        {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40" aria-label="Clear"><X size={16} /></button>}
        {matches.length > 0 && (
          <div data-testid="onfield-search-results" className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-white/10 bg-[#0d0d0d] shadow-2xl overflow-hidden">
            {matches.map((p) => (
              <button key={p.id} data-testid={`onfield-pin-result-${p.id}`} onClick={() => pin(p, true)} disabled={busy === p.id} className="w-full flex items-center gap-3 px-4 h-14 text-left hover:bg-white/5 border-b border-white/5 last:border-b-0">
                <Pin size={16} className="text-[#C41E24]" /><span className="font-semibold text-sm truncate">{p.name || p.email}</span><span className="ml-auto text-[10px] uppercase tracking-wider text-white/40">Pin</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div data-testid="onfield-drill-chips" className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
        {CHIPS.map((c) => (
          <button key={c.id || "session"} data-testid={`onfield-chip-${c.id || "session"}`} onClick={() => setChip(c.id)} aria-pressed={chip === c.id}
            className={`shrink-0 h-10 px-4 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${chip === c.id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60"}`}>{c.name}</button>
        ))}
      </div>

      {data.players.length === 0 ? (
        <div data-testid="onfield-empty" className="rounded-lg border border-dashed border-white/15 p-8 text-center text-sm text-white/45">No players pinned for today. Search above or use the pin on any row in the Players tab.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <AnimatePresence>
            {data.players.map((p) => (
              <motion.div key={p.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} data-testid={`onfield-card-${p.id}`}
                className="rounded-xl border border-white/10 bg-[#141414] p-4 flex flex-col gap-4" style={{ boxShadow: "0 12px 40px rgba(0,0,0,.45)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center font-cover text-xl text-[#F5C518] shrink-0" style={{ background: "radial-gradient(circle at 35% 30%, #2a2a2a, #050505)", border: "2px solid #C41E24" }}>{p.jersey_number ? `#${p.jersey_number}` : (p.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-headings text-xl uppercase tracking-wide truncate leading-none">{p.name || p.email}</div>
                    <div className="text-[11px] text-white/45 mt-1 flex items-center gap-2 flex-wrap">
                      {[p.club_name, p.school_name].filter(Boolean).join(" · ") || "Next O Player"}
                      {p.clips_today > 0 && <span data-testid={`onfield-clips-${p.id}`} className="inline-flex items-center gap-1 text-[#D4AF37]"><BadgeCheck size={11} /> {p.clips_today} clip{p.clips_today > 1 ? "s" : ""} today</span>}
                    </div>
                  </div>
                  <button data-testid={`onfield-unpin-${p.id}`} onClick={() => pin(p, false)} disabled={busy === p.id} aria-label="Unpin" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/50 hover:text-white"><PinOff size={16} /></button>
                </div>
                <RecordButton token={token} player={p} drill={drill?.id ? drill : null} onUploaded={load} />
                <BlockGForm token={token} player={p} onLogged={load} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
